import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Edit as EditIcon,
  Star as StarIcon,
  SupportAgent as AssistantIcon,
  Add as AddIcon,

} from '@mui/icons-material';
import api from '../../api';
import { toast } from 'react-toastify';
import { adminHasPermission } from '../../utils/adminPermissions';
import ResponsiveTable from '../../components/ResponsiveTable';
import LastSeenInfo from '../../components/LastSeenInfo';

// The three tutor login roles. Anything else falls back to a plain chip.
const ROLE_CHIP_COLOR = {
  'Mobile Tutor': 'info',
  'Tutor Assistant': 'secondary',
  Tutor: 'default',
};

const TutorList = () => {
  const navigate = useNavigate();
  const canUpdateTutors = adminHasPermission('update-tutors');
  
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalRecords, setTotalRecords] = useState(0);

  useEffect(() => {
    fetchTutors();
  }, [page, rowsPerPage, searchTerm]);

  const fetchTutors = async () => {
    setLoading(true);
    try {
      const response = await api.get('admin/tutors', {
        params: {
          page: page + 1,
          per_page: rowsPerPage,
          search: searchTerm,
        },
      });
      if (response.data.success) {
        const data = response.data.data;
        setTutors(data.data || []);
        setTotalRecords(data.total ?? 0);
      }
    } catch (error) {
      console.error('Error fetching tutors:', error);
      toast.error('Failed to fetch tutors');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box
      sx={{
        p: 3,
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Typography component="h1" sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 700, color: '#111827', lineHeight: 1.3, mb: 2 }}>
          Tutor Management
        </Typography>
        <Typography variant="body1" sx={{ color: '#5a6c7d', ml: 1 }}>
          List of registered tutors. From here you can manage tutor profiles, teaching preferences, and course assignments.
        </Typography>
      </Box>

      <Card
        sx={{
          borderRadius: 4,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(20px)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <TextField
              placeholder="Search tutors..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                width: 350,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  backgroundColor: 'white',
                  '&:hover fieldset': { borderColor: '#667eea' },
                  '&.Mui-focused fieldset': { borderColor: '#667eea' },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            {canUpdateTutors && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/admin/tutor/add')}
                sx={{
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  },
                }}
              >
                Add Tutor
              </Button>
            )}
          </Box>

          <ResponsiveTable component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid rgba(0,0,0,0.05)' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)' }}>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Tutor</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Assignments</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Groups</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Last seen</TableCell>
                  {canUpdateTutors && (
                    <TableCell sx={{ fontWeight: 600, py: 2 }} align="right">Actions</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={canUpdateTutors ? 7 : 6} align="center" sx={{ py: 8 }}>
                      <CircularProgress size={40} sx={{ mb: 2 }} />
                      <Typography color="text.secondary">Loading tutors...</Typography>
                    </TableCell>
                  </TableRow>
                ) : tutors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canUpdateTutors ? 7 : 6} align="center" sx={{ py: 8 }}>
                      <Typography color="text.secondary">No tutors found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  tutors.map((tutor) => (
                    <TableRow
                      key={tutor.id}
                      hover
                      sx={{ '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.04)' } }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            sx={{
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              width: 40,
                              height: 40,
                              fontSize: '1rem',
                              fontWeight: 600,
                            }}
                          >
                            {tutor.first_name?.[0] || <PersonIcon />}
                          </Avatar>
                          <Typography variant="body2" fontWeight={600} color="#2d3748">
                            {tutor.first_name} {tutor.last_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={tutor.role_name || 'Tutor'}
                          size="small"
                          color={ROLE_CHIP_COLOR[tutor.role_name] || 'default'}
                          sx={{ fontWeight: 600, borderRadius: '6px' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#4a5568' }}>
                          <EmailIcon fontSize="small" sx={{ opacity: 0.7 }} />
                          {tutor.email}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          <Chip
                            label={`${tutor.tutor_courses_count ?? 0} courses`}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(102, 126, 234, 0.1)',
                              color: '#667eea',
                              fontWeight: 600,
                              borderRadius: '6px',
                            }}
                          />
                          {(tutor.mobile_tutor_students_count ?? 0) > 0 && (
                            <Chip
                              label={`${tutor.mobile_tutor_students_count} students`}
                              size="small"
                              sx={{
                                bgcolor: 'rgba(2, 136, 209, 0.1)',
                                color: '#0288d1',
                                fontWeight: 600,
                                borderRadius: '6px',
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {(tutor.groups ?? []).length === 0 ? (
                          <Typography variant="caption" color="text.secondary">
                            No groups
                          </Typography>
                        ) : (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 280 }}>
                            {(tutor.groups ?? []).map((g) => (
                              <Tooltip
                                key={`${g.course_plan_id}-${g.role}`}
                                title={`${g.course_name || 'Course'} — ${
                                  g.role === 'tutor' ? 'Lead tutor' : 'Tutor Assistant'
                                }${g.is_active ? '' : ' (ended)'}`}
                              >
                                <Chip
                                  size="small"
                                  label={g.group_name}
                                  icon={g.role === 'tutor' ? <StarIcon /> : <AssistantIcon />}
                                  sx={{
                                    fontWeight: 600,
                                    borderRadius: '6px',
                                    opacity: g.is_active ? 1 : 0.55,
                                    bgcolor: g.role === 'tutor' ? '#2e7d3218' : '#0277bd18',
                                    color: g.role === 'tutor' ? '#2e7d32' : '#0277bd',
                                    '& .MuiChip-icon': {
                                      color: g.role === 'tutor' ? '#2e7d32' : '#0277bd',
                                    },
                                  }}
                                />
                              </Tooltip>
                            ))}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <LastSeenInfo user={tutor} />
                      </TableCell>
                      {canUpdateTutors && (
                      <TableCell align="right">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => navigate(`/admin/tutor/edit/${tutor.id}`)}
                          sx={{
                            borderRadius: 2,
                            borderColor: '#667eea',
                            color: '#667eea',
                            fontWeight: 600,
                            '&:hover': {
                              borderColor: '#3B2A9F',
                              background: 'rgba(59, 42, 159, 0.04)',
                            },
                          }}
                        >
                          Edit Profile
                        </Button>
                      </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ResponsiveTable>

          <TablePagination
            component="div"
            count={totalRecords}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ borderTop: '1px solid rgba(0,0,0,0.05)' }}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default TutorList;
